import React, { useState, useEffect, useRef } from 'react';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Badge,
  alpha,
  CircularProgress,
  Button,
  Paper,
  Chip,
  Container,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  ReceiptLong as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Payments as PaymentsIcon,
  InsertChart as InsightIcon,
  Description as DocumentIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  NightlightRound as DarkModeIcon,
  LightMode as LightModeIcon,
  Business as BusinessIcon,
  Store as VendorsIcon,
  AccountBalance as BankBookIcon,
  Person as PersonIcon,
  CheckCircle,
  Snooze,
  Assessment as AssessmentIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  HelpOutline as HelpIcon,
  History as HistoryIcon,
  SupportAgent as SupportIcon,
  Receipt as CashBookIcon,
  SwapHoriz as SwapIcon,
  BugReport as BugReportIcon,
  CloudUpload as CloudUploadIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { getDueDates, getOverduePayments, DueDate } from '../../utils/dueDates';
import { updateNotificationStatus } from '../../utils/notificationUtils';
import DueDatePopup from '../notifications/DueDatePopup';
import GlobalActionButton from '../common/GlobalActionButton';
import AppBar from '../layout/AppBar';
import { useTheme } from '../../context/ThemeContext';
import NetworkStatusIndicator from '../common/NetworkStatusIndicator';
import AnimatedLoader from '../common/AnimatedLoader';
import { useSetupGuide } from '../../context/SetupGuideContext';
import SetupGuide from '../SetupGuide';
// Removed ProfileCheck, ProfileCompletionPrompt, ProfileCompletionGuard imports

import { addOnlineStatusListener } from '../../utils/offlineHelper';
import DashboardSwitcher from '../DashboardSwitcher';
import { isAdminEmail } from '../../config/admin';

// 3D Colored Icon Wrapper Component
const Icon3D: React.FC<{
  children: React.ReactNode;
  isActive?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: string;
}> = ({ children, isActive = false, size = 'medium', color }) => {
  const sizeMap = {
    small: 20,
    medium: 24,
    large: 28
  };

  // Define vibrant colors for different icon types
  const getIconColor = (iconName: string) => {
    const colorMap: { [key: string]: string } = {
      'Dashboard': '#4F46E5', // Indigo
      'Sales': '#059669', // Emerald
      'Purchases': '#DC2626', // Red
      'Inventory': '#7C3AED', // Violet
      'Cash Book': '#0891B2', // Cyan
      'Bank Book': '#0D9488', // Teal
      'Expenses': '#EA580C', // Orange
      'Invoices': '#BE185D', // Pink
      'POS Billing': '#EC4899', // Pinkish-magenta
      'Clients': '#2563EB', // Blue
      'Vendors': '#16A34A', // Green
      'Business Insights': '#9333EA', // Purple
      'Transactions': '#CA8A04', // Yellow
      'Reports': '#DC2626', // Red
      'Payroll': '#0891B2', // Cyan
      'Settings': '#6B7280', // Gray
      'Testing Tools': '#EF4444', // Red
      'Receivables': '#0D9488', // Teal
      'Payables': '#DC2626', // Red
    };
    return colorMap[iconName] || color || '#6B7280';
  };

  const iconColor = color || getIconColor(children?.toString() || '');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: sizeMap[size],
        height: sizeMap[size],
        transform: isActive ? 'translateZ(8px)' : 'translateZ(4px)',
        transformStyle: 'preserve-3d',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateZ(12px) scale(1.1)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isActive
            ? `linear-gradient(135deg, ${iconColor}20, ${iconColor}40)`
            : `linear-gradient(135deg, ${iconColor}15, ${iconColor}25)`,
          borderRadius: '8px',
          transform: 'translateZ(-2px)',
          boxShadow: isActive
            ? `0 8px 25px ${iconColor}30, 0 4px 10px ${iconColor}20`
            : `0 4px 15px ${iconColor}20, 0 2px 5px ${iconColor}15`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          background: isActive
            ? `linear-gradient(135deg, ${iconColor}30, ${iconColor}10)`
            : `linear-gradient(135deg, ${iconColor}20, ${iconColor}05)`,
          borderRadius: '6px',
          transform: 'translateZ(1px)',
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          color: isActive ? iconColor : iconColor,
          filter: isActive
            ? `drop-shadow(0 2px 4px ${iconColor}40)`
            : `drop-shadow(0 1px 2px ${iconColor}30)`,
          transition: 'all 0.3s ease',
          '& svg': {
            fontSize: sizeMap[size],
          }
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const drawerWidth = 220;
const collapsedDrawerWidth = 70;

// Mobile drawer width should be slightly larger for touch targets
const mobileDrawerWidth = 280;

interface AppBarProps {
  open?: boolean;
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  padding: theme.spacing(2, 3),
  '& .MuiTypography-root': {
    background: 'var(--primary-gradient)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  margin: '4px 8px',
  borderRadius: '10px',
  transition: 'all 0.2s ease',
  padding: '8px 12px',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  ...(active && {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '4px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '0 4px 4px 0',
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    },
  }),
}));

// Animation variants
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -10
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.04,
      duration: 0.25,
      ease: "easeOut"
    }
  }),
};

const logoVariants = {
  hidden: {
    opacity: 0,
    y: -10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const notificationVariants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 }
};

const SearchBar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 40,
  borderRadius: 20,
  padding: theme.spacing(0, 2),
  width: 320,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

interface NavItem {
  title: string;
  path: string;
  icon: (isActive: boolean) => React.ReactNode;
  /** Which roles can see this item. If undefined, only admin can see it. */
  roles: string[];
}

// ─── Role Definitions ────────────────────────────────────────────────────────
// admin        → business owner, full access
// accountant   → finances only (invoices, cashbook, bankbook, expenses, reports)
// cashier      → POS billing + inventory only
// manager      → everything except payroll, settings, staff management
// viewer       → read-only dashboard + reports
// ─────────────────────────────────────────────────────────────────────────────

const ALL_STAFF = ['admin', 'accountant', 'cashier', 'manager', 'viewer'];

const getModuleAccessForTitle = (title: string, accessMap: any): 'none' | 'view' | 'edit' => {
  if (!accessMap) return 'none';
  const t = title.toLowerCase();
  if (t === 'dashboard') return accessMap.dashboard || 'none';
  if (['sales', 'pos billing', 'invoices', 'quotations', 'clients'].includes(t)) return accessMap.pos || 'none';
  if (['purchases', 'inventory', 'vendors'].includes(t)) return accessMap.inventory || 'none';
  if (['cash book', 'bank book', 'receivables', 'payables', 'transactions'].includes(t)) return accessMap.finance || 'none';
  if (t === 'expenses') return accessMap.expenses || 'none';
  if (['reports', 'business insights'].includes(t)) return accessMap.reports || 'none';
  if (t === 'payroll') return accessMap.payroll || 'none';
  if (t === 'leave management') return accessMap.leaves || 'none';
  if (t === 'staff management') return accessMap.staff || 'none';
  if (['settings', 'import data'].includes(t)) return accessMap.settings || 'none';
  return 'none';
};

const hasAccess = (item: NavItem, profile: any) => {
  if (isAdminEmail(profile?.email)) return true;
  if (profile?.role === 'admin' || profile?.role === 'super_admin') return true;
  
  const accessMap = profile?.hrDetails?.module_access;
  if (!accessMap) {
    // Fallback to legacy role based access
    return item.roles.includes(profile?.role || 'user');
  }
  
  const access = getModuleAccessForTitle(item.title, accessMap);
  return access === 'view' || access === 'edit';
};

const mainNavItems: NavItem[] = [
  { title: 'Dashboard',    path: '/dashboard',   roles: ALL_STAFF,                                           icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#4F46E5"><DashboardIcon /></Icon3D> },
  { title: 'Sales',        path: '/sales',        roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#059669"><ReceiptIcon /></Icon3D> },
  { title: 'Purchases',    path: '/purchases',    roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#DC2626"><ShoppingCartIcon /></Icon3D> },
  { title: 'Inventory',    path: '/inventory',    roles: ['admin', 'cashier', 'manager'],                     icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#7C3AED"><InventoryIcon /></Icon3D> },
  { title: 'Cash Book',    path: '/cashbook',     roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#0891B2"><CashBookIcon /></Icon3D> },
  { title: 'Bank Book',    path: '/bankbook',     roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#0D9488"><BankBookIcon /></Icon3D> },
  { title: 'Expenses',     path: '/expenses',     roles: ALL_STAFF,                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#EA580C"><PaymentsIcon /></Icon3D> },
  { title: 'Invoices',     path: '/invoices',     roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#BE185D"><DocumentIcon /></Icon3D> },
  { title: 'POS Billing',  path: '/pos',          roles: ['admin', 'cashier', 'manager'],                     icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#EC4899"><ReceiptIcon /></Icon3D> },
  { title: 'Quotations',   path: '/quotations',   roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#BE185D"><DocumentIcon /></Icon3D> },
  { title: 'Clients',      path: '/clients',      roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#2563EB"><BusinessIcon /></Icon3D> },
  { title: 'Vendors',      path: '/vendors',      roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#16A34A"><VendorsIcon /></Icon3D> },
  { title: 'Receivables',  path: '/receivables',  roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#0D9488"><BankBookIcon /></Icon3D> },
  { title: 'Payables',     path: '/payables',     roles: ['admin', 'accountant', 'manager'],                  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#DC2626"><PaymentsIcon /></Icon3D> },
];

const otherNavItems: NavItem[] = [
  { title: 'Business Insights', path: '/sales-insights', roles: ['admin', 'accountant', 'manager', 'viewer'],  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#9333EA"><InsightIcon /></Icon3D> },
  { title: 'Transactions',      path: '/transactions',   roles: ['admin', 'accountant', 'manager'],             icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#CA8A04"><HistoryIcon /></Icon3D> },
  { title: 'Reports',           path: '/reports',        roles: ['admin', 'accountant', 'manager', 'viewer'],  icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#DC2626"><DocumentIcon /></Icon3D> },
  { title: 'Payroll',           path: '/payroll',        roles: ['admin', 'accountant'],                        icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#0891B2"><PeopleIcon /></Icon3D> },
  { title: 'Leave Management',  path: '/leaves',         roles: ALL_STAFF,                                      icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#10B981"><EventIcon /></Icon3D> },
  { title: 'Staff Management',  path: '/staff-management', roles: ['admin'],                                    icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#4F46E5"><PeopleIcon /></Icon3D> },
  { title: 'Import Data',       path: '/import-data',    roles: ['admin'],                                      icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#F59E0B"><CloudUploadIcon /></Icon3D> },
  { title: 'Settings',          path: '/settings',       roles: ['admin'],                                      icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#6B7280"><SettingsIcon /></Icon3D> },
];

const adminNavItems: NavItem[] = [
  { title: 'Overview',        path: '/admin?tab=0', roles: ['super_admin'], icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#4F46E5"><DashboardIcon /></Icon3D> },
  { title: 'Support Staff',   path: '/admin?tab=1', roles: ['super_admin'], icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#059669"><SupportIcon /></Icon3D> },
  { title: 'Pending Invites', path: '/admin?tab=2', roles: ['super_admin'], icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#CA8A04"><PersonIcon /></Icon3D> },
  { title: 'All Users',       path: '/admin?tab=3', roles: ['super_admin'], icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#2563EB"><PeopleIcon /></Icon3D> },
  { title: 'Audit Logs',      path: '/admin?tab=4', roles: ['super_admin'], icon: (isActive) => <Icon3D isActive={isActive} size="small" color="#DC2626"><AssessmentIcon /></Icon3D> },
];

// Add Typewriter animation component
const TypewriterText: React.FC<{ text: string }> = ({ text }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return (
    <Typography
      variant="h6"
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {displayText}
    </Typography>
  );
};

// Add Floating Invoice Button component
const FloatingInvoiceButton: React.FC = () => {
  const navigate = useNavigate();
  const theme = useMuiTheme();

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
      }}
    >
      <Tooltip title="Create New Invoice">
        <IconButton
          onClick={() => navigate('/invoices/new')}
          sx={{
            width: 56,
            height: 56,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <ReceiptIcon />
        </IconButton>
      </Tooltip>
    </motion.div>
  );
};

interface OverduePayment {
  id: string;
  type: 'receivable' | 'payable';
  dueDate: string;
  name: string;
  amount: number;
}

const NetworkStatusWrapper = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    const unsubscribe = addOnlineStatusListener(setIsOnline);
    return () => unsubscribe();
  }, []);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <NetworkStatusIndicator />
      <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
        {isOnline ? 'Online' : 'Offline'}
      </Typography>
    </Box>
  );
};

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const muiTheme = useMuiTheme();
  const { themeMode, toggleThemeMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userProfile } = useAuth();
  const { isSetupOpen, closeSetupGuide, checkSetupStatus } = useSetupGuide();
  const isSmScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [open, setOpen] = useState(true); // Always open by default
  const [collapsed, setCollapsed] = useState(false); // New state for collapsed sidebar
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const darkMode = themeMode === 'dark';

  // For mobile, drawer should be temporary and closable
  const [mobileOpen, setMobileOpen] = useState(false);

  const [notifications, setNotifications] = useState<DueDate[]>([]);
  const [overduePayments, setOverduePayments] = useState<OverduePayment[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<DueDate | null>(null);
  const [readNotifications, setReadNotifications] = useState<string[]>([]);

  // Add state for setup guide and profile check
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const fetchNotifications = async () => {
    if (!userProfile) return;

    try {
      const [dueDates, overduePayments] = await Promise.all([
        getDueDates(),
        getOverduePayments(userProfile.uid)
      ]);

      const paymentNotifications: DueDate[] = overduePayments.map(payment => ({
        id: `payment_${payment.id}`,
        title: payment.type === 'receivable' ? 'Overdue Receivable' : 'Overdue Payable',
        dueDate: new Date(payment.dueDate),
        type: 'overdue_payment' as const,
        description: `${payment.type === 'receivable' ? 'Client' : 'Vendor'}: ${payment.name} - ₹${payment.amount}`,
        amount: payment.amount,
        status: 'overdue' as const,
        priority: 'high' as const
      }));

      const allNotifications: DueDate[] = [
        ...dueDates,
        ...paymentNotifications
      ].sort((a, b) => {
        if (a.status === 'overdue' && b.status !== 'overdue') return -1;
        if (a.status !== 'overdue' && b.status === 'overdue') return 1;
        return a.dueDate.getTime() - b.dueDate.getTime();
      });

      setNotifications(allNotifications);
      setOverduePayments(overduePayments);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // No auto-hide functionality - sidebar is always visible

  useEffect(() => {
    if (userProfile) {
      fetchNotifications();
      // Refresh notifications every hour
      const interval = setInterval(fetchNotifications, 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [userProfile]);

  // Filter notifications for current month
  const currentMonthNotifications = notifications.filter(notification => {
    const notificationDate = notification.dueDate;
    const currentDate = new Date();
    return notificationDate.getMonth() === currentDate.getMonth() &&
      notificationDate.getFullYear() === currentDate.getFullYear();
  });

  const handleMarkAsRead = (id: string) => {
    setReadNotifications(prev => [...prev, id]);
  };

  const handleMarkAllAsRead = () => {
    setReadNotifications(currentMonthNotifications.map(n => n.id));
  };

  const handleMarkComplete = async (id: string) => {
    try {
      await updateNotificationStatus(id, 'completed');
      setSelectedNotification(null);
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as complete:', error);
    }
  };

  const handleRemindLater = async (id: string) => {
    try {
      const notification = notifications.find(n => n.id === id);
      if (notification) {
        const nextDay = new Date(notification.dueDate);
        nextDay.setDate(nextDay.getDate() + 1);
        await updateNotificationStatus(id, 'pending', nextDay);
        setSelectedNotification(null);
        await fetchNotifications();
      }
    } catch (error) {
      console.error('Error setting reminder:', error);
    }
  };

  // Check for due notifications
  useEffect(() => {
    const checkDueNotifications = () => {
      const now = new Date();
      const dueNotifications = currentMonthNotifications.filter(notification => {
        const dueDate = new Date(notification.dueDate);
        return dueDate.toDateString() === now.toDateString() &&
          !readNotifications.includes(notification.id);
      });

      if (dueNotifications.length > 0 && !selectedNotification) {
        setSelectedNotification(dueNotifications[0]);
      }
    };

    // Check every minute
    const interval = setInterval(checkDueNotifications, 60000);
    checkDueNotifications(); // Initial check

    return () => clearInterval(interval);
  }, [currentMonthNotifications, readNotifications, selectedNotification]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigateToProfile = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const getUserInitials = () => {
    if (!userProfile?.displayName) return 'U';
    return userProfile.displayName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if profile is complete when userProfile changes
  // Profile check logic removed - handled by App.tsx routing


  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%" }}>
      <AppBar
        open={!collapsed}
        handleDrawerOpen={handleDrawerToggle}
        extraComponent={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DashboardSwitcher variant="icon" size="small" />
          </Box>
        }
      />

      <Drawer
        sx={{
          width: isMobile ? mobileDrawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? mobileDrawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth),
            boxSizing: 'border-box',
            border: 'none',
            backgroundColor: darkMode ? alpha(muiTheme.palette.background.paper, 0.8) : '#F1F5F9',
            transition: 'width 0.3s ease',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
      >
        <DrawerHeader sx={{ justifyContent: 'space-between', px: collapsed ? 1 : 3, py: 2 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={logoVariants}
            style={{ width: '100%' }}
          >
            <LogoContainer>
              <motion.div variants={logoVariants} initial="hidden" animate="visible">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: collapsed ? 'center' : 'flex-start' }}>
                  <img
                    src="/assets/akmobooks.com.svg"
                    alt="AkMo Books Logo"
                    style={{ height: '23px', width: 'auto' }}
                  />
                  {!collapsed && (
                    <Typography variant="h6" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1.1rem' }}>
                      AkMo Books
                    </Typography>
                  )}
                </Box>
              </motion.div>
            </LogoContainer>
          </motion.div>

          {!collapsed && (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DashboardSwitcher variant="icon" size="small" />
            </Box>
          )}
        </DrawerHeader>

        <Divider sx={{ opacity: 0.1 }} />

        {!collapsed && (
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', my: 1 }}>
            <DashboardSwitcher variant="button" size="small" />
          </Box>
        )}



        <Box sx={{ overflow: 'auto', my: 2, px: 1 }}>
          <List component="nav" disablePadding>
            {(() => {
              const isSuperAdmin = isAdminEmail(userProfile?.email);
              const role = userProfile?.role || 'user';
              
              // Super admins see the dedicated admin nav
              const itemsToRender = isSuperAdmin
                ? adminNavItems
                : mainNavItems.filter(item => hasAccess(item, userProfile));

              return itemsToRender.map((item, index) => {
              const isActive = isSuperAdmin
                ? (location.pathname + location.search === item.path) || (item.path === '/admin?tab=0' && location.pathname === '/admin' && !location.search)
                : location.pathname === item.path;

              return (
                <motion.div
                  key={item.path}
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  variants={itemVariants}
                >
                  <Tooltip title={collapsed ? item.title : ''} placement="right" arrow>
                    <StyledListItemButton
                      active={isActive}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 1 : 2,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 40, justifyContent: 'center' }}>
                        {item.icon(location.pathname === item.path)}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontWeight: location.pathname === item.path ? 600 : 500,
                            fontSize: '0.95rem'
                          }}
                        />
                      )}
                    </StyledListItemButton>
                  </Tooltip>
                </motion.div>
              );
            })})()}
          </List>

          {!isAdminEmail(userProfile?.email) && (
            <>
              <Divider sx={{ mt: 2, opacity: 0.2 }} />

              {!collapsed && (
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: muiTheme.palette.primary.main,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 18,
                        backgroundColor: muiTheme.palette.primary.main,
                        borderRadius: 1,
                        mr: 1.5,
                        display: 'inline-block'
                      }}
                    />
                    Reports & Admin
                  </Typography>
                </Box>
              )}

              <List component="nav" disablePadding>
                {(() => {
                  const role = userProfile?.role || 'user';
                  const filteredOther = otherNavItems.filter(item => hasAccess(item, userProfile));
                  
                  return filteredOther.map((item, index) => (
                  <motion.div
                    key={item.path}
                    custom={index + mainNavItems.length}
                    initial="hidden"
                    animate="visible"
                    variants={itemVariants}
                  >
                    <Tooltip title={collapsed ? item.title : ''} placement="right" arrow>
                      <StyledListItemButton
                        active={location.pathname === item.path}
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          px: collapsed ? 1 : 2,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 40, justifyContent: 'center' }}>
                          {item.icon(location.pathname === item.path)}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              fontWeight: location.pathname === item.path ? 600 : 500,
                              fontSize: '0.85rem'
                            }}
                          />
                        )}
                      </StyledListItemButton>
                    </Tooltip>
                  </motion.div>
                ))})()}
              </List>
            </>
          )}
        </Box>

        <Box sx={{ mt: 'auto', mb: 0, p: 0 }}>
          {/* Add Offline Status Toggle */}


          {/* Smaller Contact Support Button */}
          <Box sx={{ px: 2, pb: 2, pt: 0 }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              startIcon={<SupportIcon fontSize="small" />}
              onClick={() => {
                // Only open if user explicitly clicks (don't auto-open)
                setShowSetupGuide(true);
              }}
              sx={{
                borderRadius: 2,
                px: 2,
                fontSize: '0.8rem',
                textTransform: 'none',
                justifyContent: 'flex-start'
              }}
            >
              Contact Support
            </Button>
          </Box>
        </Box>
      </Drawer >

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0.5, sm: 1 },
          width: {
            xs: '100%',
            sm: `calc(100% - ${isMobile ? mobileDrawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth)}px)`
          },
          marginTop: '56px',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
          {children}
        </Container>
      </Box>

      <Menu
        id="notifications-menu"
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationsClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            borderRadius: 2,
            minWidth: 300,
            maxHeight: 400,
            overflow: 'auto',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Current Month Notifications
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </Typography>
        </Box>
        <Divider />
        {currentMonthNotifications.map((notification) => (
          <MenuItem
            key={notification.id}
            sx={{
              bgcolor: readNotifications.includes(notification.id) ? 'action.hover' : 'background.paper',
              transition: 'background-color 0.3s ease',
            }}
          >
            <Box sx={{ py: 0.5, width: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight="medium">
                  {notification.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: notification.status === 'overdue' ? 'error.main' :
                        notification.status === 'due' ? 'warning.main' : 'success.main',
                      fontWeight: 'bold'
                    }}
                  >
                    {notification.status === 'overdue' ? 'Overdue' :
                      notification.status === 'due' ? 'Due Soon' : 'Upcoming'}
                  </Typography>
                  {!readNotifications.includes(notification.id) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                    >
                      <CheckCircle fontSize="small" color="primary" />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {notification.description}
              </Typography>
              {notification.amount && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Amount: ₹{notification.amount.toLocaleString('en-IN')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Due: {notification.dueDate.toLocaleDateString()}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {currentMonthNotifications.length === 0 && (
          <MenuItem>
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No notifications for this month
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {
        selectedNotification && (
          <DueDatePopup
            notification={selectedNotification}
            open={true}
            onClose={() => setSelectedNotification(null)}
            onMarkComplete={handleMarkComplete}
            onRemindLater={handleRemindLater}
          />
        )
      }

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            borderRadius: 2,
            minWidth: 200,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {userProfile?.displayName || 'User Name'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userProfile?.email || 'user@example.com'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleNavigateToProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      <GlobalActionButton />



      {/* Setup Guide Dialog */}
      {
        showSetupGuide && (
          <SetupGuide
            open={showSetupGuide}
            onClose={() => setShowSetupGuide(false)}
          />
        )
      }


    </Box >
  );
};

const DashboardLayoutWithAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [localLoading, setLocalLoading] = useState(true);

  // Show loading screen for 3 seconds to ensure proper initialization and caching
  useEffect(() => {
    if (!loading) {
      // First check for authentication immediately but don't disable loading yet
      if (!userProfile) {
        setLocalLoading(false);
        navigate('/login');
        return;
      }

      // Show loading screen for 3 seconds while cache initializes
      const timer = setTimeout(() => {
        setLocalLoading(false);
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [userProfile, loading, navigate]);

  if (loading || localLoading) {
    return <AnimatedLoader message="Loading your dashboard... Please wait while we prepare your data" />;
  }

  return (
    <DashboardLayout>{children}</DashboardLayout>
  );
};

export default DashboardLayoutWithAuth;